<template>
  <v-app id="app" class="app">
    <div class="app__layout">
      <div class="app__layout__left" :class="{ 'slide-right': toggle }">
        <sidebar :version="version" class="app__layout__left__sidebar" />
      </div>
      <div class="app__layout__right" :class="{ 'slide-right': toggle }">
        <banner
          v-if="user"
          class="app__layout__right__banner"
          :class="[show ? 'show' : 'hide']"
        />
        <navigation
          v-if="user"
          class="app__layout__right__nav"
          :class="[show ? 'show' : 'hide']"
        />
        <v-content class="app__layout__right__content">
          <!-- refresh snack -->
          <v-snackbar v-model="refreshSnack" :timeout="timeout" top>
            {{ refreshSnackWithBtnText }}
            <v-spacer />
            <v-btn dark text color="#00f500" @click.native="refreshApp">{{
              refreshSnackBtnText
            }}</v-btn>
            <v-btn icon @click="refreshSnack = false">
              <font-awesome-icon
                icon="times"
                size="lg"
                :style="{ color: 'white' }"
              />
            </v-btn>
          </v-snackbar>

          <!-- Global modals -->
          <add-merchant v-if="showMerchantModal" />
          <add-outlet v-if="showOutletModal" />
          <image-cropper v-if="showCropperModal" />
          <offer-info v-if="showInfoModal" />
          <offer v-if="showOfferModal" />
          <router-view />
        </v-content>
      </div>
    </div>
  </v-app>
</template>

<script>
// Views
import Banner from "@/components/Banner";
import Navigation from "@/components/Navigation";
import Sidebar from "@/components/Sidebar";
import NotFound from "@/views/NotFound";

// Modals
import addMerchant from "@/components/merchants/addMerchant";
import addOutlet from "@/components/outlets/addOutlet";
import imageCropper from "@/components/cropper/imageCropper";
import offerInfo from "@/components/offer/offerInfo";
import offer from "@/components/offer/offer";

import { mapActions, mapState } from "vuex";
import axios from "axios";

export default {
  name: "App",

  components: {
    Banner,
    Navigation,
    Sidebar,
    addMerchant,
    addOutlet,
    imageCropper,
    offerInfo,
    offer
  },

  data() {
    return {
      //versioning
      version: "0.0.2",
      refreshing: false,
      registration: null,
      refreshSnackBtnText: "REFRESH",
      refreshSnackWithBtnText: "New update available",
      refreshSnack: false,
      timeout: 0,
      //data
      show: true,
      lastScrollPos: 0
    };
  },

  methods: {
    addOffers() {
      this.getOffers();
    },

    onScroll() {
      const currentPos =
        window.pageYOffset || document.documentElement.scrollTop;

      if (currentPos < 0) {
        return;
      }

      // Hide on scroll down
      if (Math.abs(currentPos - this.lastScrollPos) < 200) {
        return;
      }

      // Show on scroll up
      if (Math.abs(currentPos == this.lastScrollPos + 10)) {
        return;
      }

      this.show = currentPos < this.lastScrollPos;
      this.lastScrollPos = currentPos;
    },

    showRefreshUI(e) {
      // Display a snackbar inviting the user to refresh/reload the app due
      // to an app update being available.
      // The new service worker is installed, but not yet active.
      // Store the ServiceWorkerRegistration instance for later use.
      this.registration = e.detail;
      this.refreshSnackBtnText = "Refresh";
      this.refreshSnackWithBtnText = "New version available!";
      this.refreshSnack = true;
    },
    refreshApp() {
      this.refreshSnack = false;
      // Protect against missing registration.waiting.
      if (!this.registration || !this.registration.waiting) {
        return;
      }
      this.registration.waiting.postMessage("skipWaiting");
    }
  },

  computed: {
    ...mapState({
      auth: state => state.auth,
      ui: state => state.ui
    }),

    user() {
      return this.auth.user;
    },

    toggle() {
      return this.ui.toggle;
    },

    showMerchantModal() {
      return this.ui.toggleMerchantModal;
    },

    showOutletModal() {
      return this.ui.toggleOutletModal;
    },

    showCropperModal() {
      return this.ui.toggleCropperModal;
    },

    showInfoModal() {
      return this.ui.toggleInfoModal;
    },

    showOfferModal() {
      return this.ui.toggleOfferModal;
    }
  },

  watch: {
    "ui.toggleInfoModal": val => {
      document.querySelector("body").style.position = val ? "fixed" : "";
    }
  },

  mounted() {
    window.addEventListener("scroll", this.onScroll);
  },

  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll);
  },

  created() {
    // Listen for swUpdated event and display refresh snackbar as required.
    document.addEventListener("swUpdated", this.showRefreshUI, { once: true });
    // Refresh all open app tabs when a new service worker is installed.
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (this.refreshing) return;
      this.refreshing = true;
      window.location.reload();
    });
  }
};
</script>

<style lang="sass">
@import @/styles/variables.sass
@import @/styles/mixins.sass
@import @/styles/base.sass

.app
  display: flex
  flex-flow: column
  position: absolute

  &__layout
    display: flex
    flex-flow: row
    overflow: hidden

    &__left
      margin-left: -220px
      width: 220px
      height: 100%
      position: fixed
      transition: all 200ms ease-in

      &__sidebar
        height: 100%

    &__right
      position: relative
      width: 100vw
      height: 100%
      background: $bg-color
      overflow-y: auto
      transition: all 200ms ease-in

      &__banner
        position: fixed
        height: 40px
        z-index: 200

      &__nav
        position: fixed
        height: 40px
        top: 40px
        z-index: 100

      &__content
        margin-top: 80px
        transition: margin-left .5s
        background-color: $bg-color

      .enter-left-enter-active, .enter-left-leave-active
        transition: all .3s cubic-bezier(0.175, 0.885, 0.32, 1.15)

      .enter-left-enter, .enter-left-leave-to
        transform: translateX(-200px)
        opacity: 0

.slide-right
  transition: all 200ms ease-in
  transform: translate3d(220px, 0, 0)

.show
  transform: translate3d(0, 0%, 0)
  transition: all .3s

.hide
  transform: translate3d(0, -200%, 0)
  transition: all .3s
</style>
